import type { TCreatedPdf } from 'pdfmake/build/pdfmake';
import { loadImageDataUrl } from '../../storage/images';
import type { Margins, PageSize } from 'pdfmake/interfaces';

/**
 * Rich-text HTML often contains entities like &nbsp; Browsers render those as characters,
 * but our tag-stripping leaves the literal "&nbsp;" in the string — pdfmake then prints it.
 */
function decodeHtmlEntities(input: string): string {
  return input
    .replace(/&nbsp;/gi, '\u00a0')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
}

// Helper function to convert HTML to plain text for PDF generation
function htmlToPdfContent(text: string): any[] {
  const hasHtmlTags = /<[^>]+>/.test(text);

  if (!hasHtmlTags) {
    return [{ text: decodeHtmlEntities(text.trim()) }];
  }

  // It contains HTML tags (new system) - convert to plain text
  let cleanText = text.trim();

  // First, handle specific tags that might need special treatment
  // Convert <br> tags to line breaks
  cleanText = cleanText.replace(/<br\s*\/?>/gi, '\n');

  // Convert <p> tags to double line breaks (paragraph spacing)
  cleanText = cleanText.replace(/<\/p>\s*<p[^>]*>/gi, '\n\n');
  cleanText = cleanText.replace(/<p[^>]*>/gi, '');
  cleanText = cleanText.replace(/<\/p>/gi, '\n');

  // Strip all remaining HTML tags (including strong, em, etc.) but keep their content
  cleanText = cleanText.replace(/<[^>]+>/g, '');

  // Clean up extra whitespace but preserve intentional line breaks
  cleanText = cleanText.replace(/[ \t]+/g, ' '); // Replace multiple spaces/tabs with single space
  cleanText = cleanText.replace(/\n\s+/g, '\n'); // Remove spaces after newlines
  cleanText = cleanText.replace(/\s+\n/g, '\n'); // Remove spaces before newlines
  cleanText = cleanText.replace(/\n{3,}/g, '\n\n'); // Limit to double newlines max
  cleanText = decodeHtmlEntities(cleanText.trim());

  return [{ text: cleanText }];
}

// Helper function to convert HTML to pdfmake structured content with formatting preserved
function htmlToPdfContentWithFormatting(text: string): any[][] {
  const hasHtmlTags = /<[^>]+>/.test(text);

  if (!hasHtmlTags) {
    const trimmed = decodeHtmlEntities(text.trim());
    if (!trimmed) return [];
    return [[{ text: trimmed }]]; // single paragraph with plain text
  }

  // Normalize HTML into paragraphs and line breaks
  let html = decodeHtmlEntities(text.trim());
  // Convert <br> to single newline
  html = html.replace(/<br\s*\/?>/gi, '\n');
  // Convert paragraph boundaries to double newlines
  html = html.replace(/<\/p>\s*<p[^>]*>/gi, '\n\n');
  // Remove remaining <p> and </p>
  html = html.replace(/<p[^>]*>/gi, '').replace(/<\/p>/gi, '\n');

  // Split into paragraphs by double newlines
  const paragraphs = html
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter((p) => p.length > 0);

  // For each paragraph, build an array of inline text objects with bold/italic
  const paragraphInlines: any[][] = paragraphs.map((paragraph) => {
    // Preserve single newlines within a paragraph
    // We'll split by newline but re-insert line breaks as '\n'
    const lines = paragraph.split(/\n/);

    const buildInlinesFromHtml = (raw: string): any[] => {
      // Handle <strong>/<b> and <em>/<i>
      const boldRegex = /<(strong|b)>([\s\S]*?)<\/(strong|b)>/gi;
      const italicRegex = /<(em|i)>([\s\S]*?)<\/(em|i)>/gi;

      // Replace with markers
      const withBoldMarkers = raw.replace(
        boldRegex,
        (_m, _t1, content) => `__BOLD_START__${content}__BOLD_END__`,
      );
      const withMarkers = withBoldMarkers.replace(
        italicRegex,
        (_m, _t1, content) => `__ITALIC_START__${content}__ITALIC_END__`,
      );

      const parts = withMarkers.split(
        /(__BOLD_START__|__BOLD_END__|__ITALIC_START__|__ITALIC_END__)/,
      );
      let isBold = false;
      let isItalic = false;
      const inlines: any[] = [];

      for (const part of parts) {
        if (!part) continue;
        if (part === '__BOLD_START__') {
          isBold = true;
          continue;
        }
        if (part === '__BOLD_END__') {
          isBold = false;
          continue;
        }
        if (part === '__ITALIC_START__') {
          isItalic = true;
          continue;
        }
        if (part === '__ITALIC_END__') {
          isItalic = false;
          continue;
        }
        // Strip any remaining tags
        const clean = decodeHtmlEntities(part.replace(/<[^>]+>/g, ''));
        if (!clean) continue;
        const obj: any = { text: clean };
        if (isBold) obj.bold = true;
        if (isItalic) obj.italics = true;
        inlines.push(obj);
      }

      return inlines.length > 0
        ? inlines
        : [{ text: decodeHtmlEntities(withMarkers.replace(/<[^>]+>/g, '')) }];
    };

    // Build inlines for each line and join with explicit line breaks
    const paragraphInline: any[] = [];
    lines.forEach((line, idx) => {
      const inlines = buildInlinesFromHtml(line);
      paragraphInline.push(...inlines);
      if (idx < lines.length - 1) {
        paragraphInline.push({ text: '\n' });
      }
    });

    return paragraphInline;
  });

  return paragraphInlines;
}

const PDF_FONTS = {
  Roboto: {
    normal:
      'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-Regular.ttf',
    bold: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-Medium.ttf',
    italics:
      'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-Italic.ttf',
    bolditalics:
      'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-MediumItalic.ttf',
  },
};

// pdfmake standard page sizes: https://github.com/bpampuch/pdfmake/blob/be1bf25fdc0cb6c77d40d661592dd6ee21d45dab/src/standardPageSizes.js
const LETTER_SIZE = {
  name: 'LETTER' as PageSize,
  width: 612,
  height: 792,
};

const DEFAULT_PAGE_MARGINS: Margins = [30, 30, 30, 30];

// Renders a printable "Signature" / "Date" block intended for ink-signing.
// Each label sits above a thin horizontal rule. Used by the PRA-826 manual
// signing flow when no captured signature image is available.
//
// The numbers are pdfmake user-space units (points). `pageMargins` removes
// 30pt from each edge, so a `LETTER_SIZE.width - 60` = 552pt usable width;
// we draw lines at 400pt to leave room on the right.
const SIGNATURE_LINE_WIDTH = 400;
const SIGNATURE_LINE_THICKNESS = 0.5;

const buildSignatureLineSection = () => [
  {
    text: 'Signature:',
    style: 'body',
    margin: [0, 16, 0, 18] as [number, number, number, number],
  },
  {
    canvas: [
      {
        type: 'line',
        x1: 0,
        y1: 0,
        x2: SIGNATURE_LINE_WIDTH,
        y2: 0,
        lineWidth: SIGNATURE_LINE_THICKNESS,
      },
    ],
  },
  {
    text: 'Date:',
    style: 'body',
    margin: [0, 24, 0, 18] as [number, number, number, number],
  },
  {
    canvas: [
      {
        type: 'line',
        x1: 0,
        y1: 0,
        x2: SIGNATURE_LINE_WIDTH,
        y2: 0,
        lineWidth: SIGNATURE_LINE_THICKNESS,
      },
    ],
  },
];

/**
 * Composes the engagement letter PDF.
 *
 * Three input cases for `signatureDataUrl`:
 *   - **Non-empty string (digital signed mode):** pass a data URL of the
 *     captured signature image and a human-readable `signatureTimestamp`.
 *     The PDF appends the signature image and a "Signed on ..." line at
 *     the bottom — this is what the public signature portal saves to Cloud
 *     Storage.
 *   - **`undefined` (manual / print mode):** `signatureTimestamp` is
 *     ignored. The PDF appends blank "Signature: __________ /
 *     Date: __________" lines for an ink signature. PRA-826 uses this mode
 *     to produce a print copy the firm hands to a client who wants to sign
 *     on paper.
 *   - **Empty string `''` (caller bug):** the digital-signature page
 *     resolves its signature ref to `?? ''` if the canvas ref is null, so
 *     an empty string here means the canvas was never populated. Bail with
 *     a warning instead of silently rendering print-mode lines — otherwise
 *     the digital path would record a "signed" letter with a blank
 *     signature, the exact bug we want to preserve from the pre-PRA-826
 *     behavior.
 *
 * Returns `undefined` when the prerequisites for any PDF are missing
 * (no firm id, no text blocks, or an empty signature string).
 */
export const composeEngagementLetterPdf = async (
  firmId: string | undefined,
  textBlocks: string[],
  signatureDataUrl: string | undefined,
  signatureTimestamp?: string,
  stripHtmlForCleanCopy: boolean = false, // false = preserve HTML formatting (default), true = strip HTML completely
): Promise<TCreatedPdf | undefined> => {
  if (!firmId) {
    console.warn('Attempting to generate a PDF without a firm ID');
    return;
  }
  if (!textBlocks) {
    console.warn('Attempting to generate a PDF without text blocks');
    return;
  }
  if (signatureDataUrl === '') {
    // Distinguish "intentional print mode" (signatureDataUrl === undefined)
    // from "caller's signature ref was null and fell through to its
    // `?? ''` default" — bail on the empty-string case so we don't record
    // a "signed" letter with a blank signature image. Matches the pre-
    // PRA-826 behavior, where the caller would warn and not upload.
    console.warn(
      'Attempting to generate a PDF with an empty signature data URL; bailing',
    );
    return;
  }

  const logoInfo = await loadImageDataUrl(firmId, 'engagementLetterLogo');
  const maxLogoWidth = LETTER_SIZE.width / 2;
  const logoWidth = Math.min(logoInfo.width, maxLogoWidth);

  // pdfmake is gigantic, so import it dynamically to reduce bundle size
  const { createPdf } = await import('pdfmake/build/pdfmake');

  const pdfDoc = createPdf(
    {
      pageSize: LETTER_SIZE,
      pageMargins: DEFAULT_PAGE_MARGINS,
      content: [
        {
          image: logoInfo.dataUrl,
          width: logoWidth,
          alignment: 'center',
        },
        {
          text: 'Engagement Letter',
          style: 'title1',
        },
        ...textBlocks.flatMap((textBlock, index) => {
          // Convert HTML to structured PDF content - strip HTML for client copy, preserve for law firm copy
          const formattedParagraphs = htmlToPdfContentWithFormatting(textBlock);
          const plainText = htmlToPdfContent(textBlock); // returns single array with one text item

          // When stripping HTML, use a single block with plain text
          if (stripHtmlForCleanCopy) {
            const blockElement: any = {
              unbreakable: false,
              style: 'body',
              text: plainText.length > 0 ? plainText[0].text : textBlock,
            };
            if (textBlocks.length > 1 && index > 0)
              blockElement.margin = [0, 8, 0, 0];
            return [blockElement];
          }

          // Otherwise, emit one block per paragraph, each with inline formatting preserved
          const blocks = (
            formattedParagraphs.length > 0
              ? formattedParagraphs
              : [[{ text: textBlock }]]
          ).map((inlines, pIdx) => {
            const block: any = {
              unbreakable: false,
              style: 'body',
              text: inlines,
            };
            // Add spacing between paragraphs and between blocks
            if (index > 0 || pIdx > 0) block.margin = [0, 8, 0, 0];
            return block;
          });

          return blocks;
        }),
        ...(signatureDataUrl
          ? [
              {
                image: signatureDataUrl,
                width: 400,
                alignment: 'left' as const,
              },
              ...(signatureTimestamp
                ? [
                    {
                      text: `Signed on ${signatureTimestamp}`,
                      style: 'body',
                      margin: [0, 10, 0, 0] as [number, number, number, number],
                    },
                  ]
                : []),
            ]
          : buildSignatureLineSection()),
      ],

      styles: {
        title1: {
          fontSize: 18,
          bold: true,
          alignment: 'center',
          margin: [0, 20, 0, 16],
        },
        body: {
          fontSize: 12,
          bold: false,
          alignment: 'left',
          margin: [0, 0, 0, 18],
          lineHeight: 1.5,
        },
      },
      defaultStyle: {
        font: 'Roboto',
      },
    },
    undefined,
    PDF_FONTS,
  );
  return pdfDoc;
};
